import type { ReactNode, FC } from "react";
import { useFormContext } from "react-hook-form";
import { ErrorMessage } from "@hookform/error-message";
import { competitionColorSystem } from "utils/ui/competitionColorSystem";
import { cx } from "@/utils/ui/uiHelpers";
import Tooltip from "./Tooltip";

interface FormFieldProps {
  label: string;
  icon?: ReactNode;
  children: ReactNode;
  className?: string;
  fieldName?: string;
  defaultMissingMessage?: string;
  showError?: boolean;
  tooltip?: string;
}

const FormField: FC<FormFieldProps> = ({
  label,
  icon,
  children,
  className = "",
  fieldName,
  defaultMissingMessage,
  tooltip,
  showError = true,
}) => {
  const {
    formState: { errors },
  } = useFormContext();

  return (
    <div className={cx(className)}>
      <label
        className={cx(
          "mb-2 flex items-center !justify-start !text-left text-sm font-medium",
          competitionColorSystem.ui.text.primary,
        )}
      >
        {icon && (
          <span
            className={cx(
              "mr-2 flex items-center pl-1 pr-0.5",
              competitionColorSystem.ui.icon.primary,
            )}
          >
            {icon}
          </span>
        )}
        {label}
        {tooltip && (
          <Tooltip
            content={tooltip}
            className="float-right inline"
            triggerClassName="ml-auto"
            iconClassName="size-5 text-zinc-800 dark:text-gray-50"
          ></Tooltip>
        )}
      </label>
      {children}
      {fieldName && showError && (
        <ErrorMessage
          errors={errors}
          name={fieldName}
          render={({ message }) => (
            <div
              className={cx("text-sm", competitionColorSystem.ui.text.error)}
            >
              {message?.toString() ||
                defaultMissingMessage ||
                "This field is required"}
            </div>
          )}
        />
      )}
    </div>
  );
};

export default FormField;
